<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="3">
          <FormulateInput
            id="group-id"
            v-model="filters.groupId"
            type="vue-select"
            :label="$t('Grupo')"
            :placeholder="$t('Todos')"
            :options="getComboGroups"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="brand-id"
            v-model="filters.brandId"
            type="vue-select"
            :label="$t('Marca')"
            :placeholder="$t('Todas')"
            :options="getComboBrands"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="day-of-week"
            v-model="filters.dayOfWeek"
            type="vue-select"
            :label="$t('Dia da semana')"
            :placeholder="$t('Todos')"
            :options="stockCountingDayOfWeekOptions()"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="active-only"
            v-model="filters.activeOnly"
            type="vue-select"
            :label="$t('Ativo')"
            :placeholder="$t('Todos')"
            :options="[
              { value: 'true', label: $t('Sim') },
              { value: 'false', label: $t('Não') },
            ]"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="table-stock-counting-rules"
        responsive
        show-empty
        striped
        class="mb-1 bordered"
        :empty-text="
          getEmptyTableMessage($tc('STOCK_COUNTING_RULE.NAME'), 'female', queryExecuted)
        "
        :fields="fields"
        :items="stockCountingRules"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="$can('Update', 'StockCountingRule')"
            :show-delete="$can('Delete', 'StockCountingRule')"
            :show-read-only="false"
            @update="showEdit(row.item)"
            @delete="onDeleteRule(row.item)"
          />
        </template>

        <template #cell(active)="{ item }">
          <span
            v-if="item.active"
            class="text-success font-weight-bold"
          >{{ $t('Sim') }}</span>
          <span
            v-else
            class="text-danger"
          >{{ $t('Não') }}</span>
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="table-stock-counting-rules"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <stock-counting-rule-sidebar
      ref="sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'StockCountingRule')"
      :main-tooltip="$t('Adicionar Regra de Contagem')"
      @click="showCreate"
    />
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EFilters,
  EGridActions,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { formulateValidation, productDomains, stockDomain } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import StockCountingRuleSidebar from './components/StockCountingRuleSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EFilters,
    StockCountingRuleSidebar,
    EGridActions,
  },

  mixins: [formulateValidation, stockDomain, productDomains],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapGetters('pages/catalog/products', ['getComboGroups', 'getComboBrands']),
    ...mapState('pages/stock/stockCountingRules', {
      stockCountingRules: 'stockCountingRules',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '70px' },
          sortable: true,
        },
        {
          label: this.$t('Grupo'),
          key: 'group.name',
          formatter: (val, index, item) => item.group?.name || '-',
          thStyle: { minWidth: '130px' },
        },
        {
          label: this.$t('Marcas'),
          key: 'brands',
          formatter: (val, index, item) =>
            item.brands?.length ? item.brands.map(b => b.name).join(', ') : '-',
          thStyle: { minWidth: '130px' },
        },
        {
          label: this.$t('Dias da semana'),
          key: 'daysOfWeek',
          class: 'text-center',
          thStyle: { minWidth: '150px' },
          formatter: value => {
            if (!value?.length) return '-'
            const options = this.stockCountingDayOfWeekOptions()
            return value
              .map(day => options.find(o => o.value === day)?.label || day)
              .join(', ')
          },
        },
        {
          label: this.$t('Mín. contagens'),
          key: 'minCountings',
          class: 'text-center',
          thStyle: { width: '110px' },
          sortable: true,
        },
        {
          label: this.$t('Ativo'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '80px' },
          sortable: true,
        },
        {
          label: this.$t('Descrição'),
          key: 'description',
          formatter: value => value || '-',
          thStyle: { minWidth: '180px' },
        },
        {
          label: this.$t('Criado por'),
          key: 'createdBy',
          formatter: value => value || '-',
          thStyle: { minWidth: '120px' },
        },
        {
          label: this.$t('Criado em'),
          key: 'createdDate',
          class: 'text-center',
          thStyle: { width: '130px' },
          formatter: value => this.$options.filters.date(value),
          sortable: true,
        },
      ]
    },
  },

  async mounted() {
    try {
      this.fetching = true
      await this.fetchProductCombos()
    } finally {
      this.fetching = false
    }
  },

  methods: {
    ...mapActions('pages/stock/stockCountingRules', [
      'fetchStockCountingRules',
      'resetFilters',
      'setCurrentPage',
      'setPageSize',
      'setFilters',
      'setRuleForm',
      'cleanRuleForm',
    ]),
    ...mapActions('pages/catalog/products', ['fetchProductCombos']),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchStockCountingRules()
          this.queryExecuted = true
        } catch (error) {
          this.showError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    async showCreate() {
      // Garante que os combos de grupo/marca estejam carregados antes de abrir.
      await this.fetchProductCombos()
      this.cleanRuleForm()
      this.$refs.sidebar.showSidebar = true
    },

    async showEdit(rule) {
      // Garante que os combos de grupo/marca estejam carregados antes de
      // vincular os valores, para que o vue-select exiba os rótulos.
      await this.fetchProductCombos()
      this.setRuleForm({
        id: rule.id,
        // O vue-select compara os valores por igualdade estrita/serialização.
        // As opções dos combos usam o id como string, então convertemos aqui
        // para que o valor selecionado seja exibido corretamente.
        groupId: rule.group?.id != null ? String(rule.group.id) : null,
        brandIds: (rule.brands || []).map(b => String(b.id)),
        daysOfWeek: rule.daysOfWeek || [],
        minCountings: rule.minCountings,
        active: rule.active,
        description: rule.description,
      })
      this.$refs.sidebar.showSidebar = true
    },

    async onDeleteRule(rule) {
      if (
        await this.confirm({
          text: this.$t('Confirma a exclusão desta regra de contagem?'),
        })
      ) {
        try {
          this.fetching = true
          await this.$store.dispatch('pages/stock/stockCountingRules/deleteRule', rule.id)
          this.showSuccess({ message: this.$t('Regra excluída com sucesso!') })
          this.getData()
        } catch (error) {
          this.showError({ error })
        } finally {
          this.fetching = false
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
